import type { Stage } from "@amzn/ads-forge-client";
import { ContentType as ForgeContentType } from "@amzn/ads-forge-client";
import {
    fetchAndStoreForgeApiCredentials,
    getForgeApiClient,
    useForgeNarrativeDownload,
    NarrativeTranslateModalButton,
    NarrativeLoader,
} from "@amzn/ads-forge-web-assets";
import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import type {
    GeneratedInsight,
    InsightType,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { SplitButton, SplitButtonItem } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import { startOfHour } from "date-fns";
import { isString } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { SUMMARY_ASSET_ERROR_MESSAGE } from "../../constants/deckDecoration";
import { G4sPlacement } from "../../constants/g4sPlacement";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../constants/telemetry";
import { useSetInsightDetailComponents } from "../../hooks/insightDetail/useSetInsightDetailComponents";
import { authStore } from "../../store/auth";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { globalWorkbenchStore } from "../../store/globalWorkbench";
import { insightDetailComponentStore } from "../../store/insightDetailComponent";
import { notificationStore, NotificationType } from "../../store/notification";
import { workbenchStore } from "../../store/workbench";
import type { CardDetail } from "../../types/card";
import { InsightPreviewFileFormats } from "../../types/insightPreviewFileFormats";
import type {
    InsightDetailOption,
    InsightDetailSelectionProps,
} from "../../utils/insightDetail.utils";
import { InsightDetailType } from "../../utils/insightDetail.utils";
import { getAdvertiserId } from "../../utils/permission.utils";
import { getPhoenixContentInfoFromStore } from "../../utils/phoenix.utils";
import {
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
    logWithTakt,
} from "../../utils/telemetry.utils";
import {
    BodyComponentBuilder,
    FooterComponentBuilder,
    Header,
    HeaderCardDetails,
    HeaderComponentBuilder,
    HeaderTitle,
} from "../common/insightDetail.components";
import {
    AddedToGlobalWorkbenchNotification,
    GlobalWorkbenchCartActionErrorNotification,
    UpdateGlobalWorkbenchNotification,
} from "../common/notification.components";
import { DeckDecorationWithLanguageModalButton } from "../createDeckDecoration/DeckDecorationWithLanguageModalButton";
import { EditInputsButton } from "../discovery/insights/generatedInsights/PackagedInsightCard/PackagedInsightsDetails.Actions";
import { InsightWorkbenchCartButton } from "../remote";

export interface ForgePackagedInsightDetails {
    details: CardDetail[];
    title: string;
    identifier: string;
    insightType: InsightType;
    downloadLoading: boolean;
    insight: GeneratedInsight;
    reset: () => void;
}

export const useSetForgePackagedInsightDetails = (
    props: ForgePackagedInsightDetails,
): (() => void) => {
    const {
        identifier,
        insightType,
        insight,
        details,
        title,
        downloadLoading,
        reset,
    } = props;

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const [forgeOutput, setForgeOutput] = useState<any>();

    const [resetStore] = useStore(insightDetailComponentStore, (state) => [
        state.reset,
    ]);
    const settings = useStore(authStore, (state) => state.settings);
    const username = useStore(authStore, (state) => state.username);
    const [areComponentsReady, setComponentsReady] = useState(false);

    const isWorkbenchForgeEnabled = useStore(
        FeatureFlagsStore,
        (state) => state.workbenchForge,
    );

    const [forgeLastChanged, setForgeLastChanged] = useState(
        new Date().toISOString(),
    );

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [getSlidesSelected, insightInCart] = useStore(
        globalWorkbenchStore,
        (state) => [state.getSlidesSelected, state.isInsightInCart(identifier)],
    );

    const [addedToCart, setAddedToCart] = useState(false);

    const [numPages, setNumPages] = useState<number>(0);

    const { downloadNarrative, downloadCurrentRenderedNarrative } =
        useForgeNarrativeDownload();

    useEffect(() => {
        const fetchCredentials = async () => {
            await fetchAndStoreForgeApiCredentials({
                stage: settings.stage as Stage,
                username,
                cognitoPoolId: settings.cognitoPoolId,
                accountId: settings.blinkAwsAccountId,
            });
        };
        void fetchCredentials();
    }, []);

    const handleMount = (props: InsightDetailSelectionProps): void => {
        const slidesFromCart = getSlidesSelected(identifier);
        const slidesFromWorkbench = getSlideIndexes(identifier);

        const slides = insightInCart ? slidesFromCart : slidesFromWorkbench;

        props.setSelectedSlides(
            Array.isArray(slides) ? new Set(slides) : slides,
        );
    };

    const handleClose = () => {
        setNumPages(0);
        setComponentsReady(false);
        resetStore();
        reset();
    };

    const packageTelemetryDetails = {
        packageName: insight.template,
        domainId: insight.domainIdentifier,
        templateId: insight.templateIdentifier,
    };

    const handleForgeVizDownload = (identifier) => {
        logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD, {
            identifier,
            insightType,
            ...packageTelemetryDetails,
        });
        const filters = FeatureFlagsStore.getState().unifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();
        logWithTakt(TELEMETRY_ID.INSIGHT_DOWNLOAD, {
            ...filters,
            insightType,
            identifier,
            ...packageTelemetryDetails,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.FORGE.VIZ_DOWNLOAD,
            PHOENIX_FEATURE.FORGE,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    insightType,
                    identifier,
                ),
            },
        );
        PhoenixMetrics.emit(
            TELEMETRY_ID.INSIGHT_DOWNLOAD,
            PHOENIX_FEATURE.DISCOVERY,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    insightType,
                    identifier,
                ),
            },
        );
        downloadNarrative(identifier, ForgeContentType.PACKAGED)
            .then(() => {
                logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_SUCCESS, {
                    identifier,
                    insightType,
                    ...packageTelemetryDetails,
                });
                PhoenixMetrics.emit(
                    TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_SUCCESS,
                    PHOENIX_FEATURE.FORGE,
                    {
                        contentInfo: getPhoenixContentInfoFromStore(
                            insightType,
                            identifier,
                        ),
                    },
                );
            })
            .catch(() => {
                logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_ERROR, {
                    identifier,
                    insightType,
                    ...packageTelemetryDetails,
                });
                PhoenixMetrics.emit(
                    TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_ERROR,
                    PHOENIX_FEATURE.FORGE,
                    {
                        contentInfo: getPhoenixContentInfoFromStore(
                            insightType,
                            identifier,
                        ),
                    },
                );
            });
    };

    const handleCustomizedDownloadView = (identifier) => {
        logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD, {
            identifier,
            insightType,
            cutomizedView: true,
            ...packageTelemetryDetails,
        });
        const filters = FeatureFlagsStore.getState().unifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();
        logWithTakt(TELEMETRY_ID.INSIGHT_DOWNLOAD, {
            ...filters,
            insightType,
            identifier,
            ...packageTelemetryDetails,
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.FORGE.VIZ_DOWNLOAD,
            PHOENIX_FEATURE.FORGE,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    insightType,
                    identifier,
                ),
            },
        );
        PhoenixMetrics.emit(
            TELEMETRY_ID.INSIGHT_DOWNLOAD,
            PHOENIX_FEATURE.DISCOVERY,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    insightType,
                    identifier,
                ),
            },
        );
        downloadCurrentRenderedNarrative(identifier)
            .then(() => {
                logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_SUCCESS, {
                    identifier,
                    insightType,
                    cutomizedView: true,
                    ...packageTelemetryDetails,
                });
                PhoenixMetrics.emit(
                    TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_SUCCESS,
                    PHOENIX_FEATURE.FORGE,
                    {
                        contentInfo: getPhoenixContentInfoFromStore(
                            insightType,
                            identifier,
                        ),
                    },
                );
            })
            .catch(() => {
                logWithTakt(TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_ERROR, {
                    identifier,
                    insightType,
                    cutomizedView: true,
                    ...packageTelemetryDetails,
                });
                PhoenixMetrics.emit(
                    TELEMETRY_ID.FORGE.VIZ_DOWNLOAD_ERROR,
                    PHOENIX_FEATURE.FORGE,
                    {
                        contentInfo: getPhoenixContentInfoFromStore(
                            insightType,
                            identifier,
                        ),
                    },
                );
            });
    };

    const PackagedInsightHeaderOption: InsightDetailOption = useMemo(() => {
        const PackagedInsightHeader: React.FunctionComponent<
            InsightDetailSelectionProps
        > = () => (
            <HeaderComponentBuilder
                loading={downloadLoading}
                headerComponents={[
                    <Header
                        numPages={numPages}
                        identifier={identifier}
                        insightType={insightType}
                    />,
                    <HeaderTitle title={title} />,
                    <HeaderCardDetails
                        details={details}
                        numPages={numPages}
                        previewFileFormat={InsightPreviewFileFormats.FORGE}
                        identifier={identifier}
                        insightType={insightType}
                        aiSummaryOverview={insight.aiSummaryOverview}
                        aiSummaryExecutive={insight.aiSummaryExecutive}
                        buttonOverride={[
                            <NarrativeTranslateModalButton
                                contentIdentifier={identifier}
                            />,
                            <DeckDecorationWithLanguageModalButton
                                identifier={identifier}
                                insightType={insightType}
                                documentUrlAsync={getDocumentUrlAsync}
                                placement={G4sPlacement.SIDE_PANEL}
                            />,
                        ]}
                    />,
                ]}
            />
        );
        return {
            type: InsightDetailType.SELECTION,
            component: PackagedInsightHeader,
        };
    }, [identifier, numPages, details, downloadLoading]);

    const PackagedInsightBodyOption: InsightDetailOption = useMemo(() => {
        const PackagedInsightBody: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => {
            const [workbenchSourceAsset, getWorkbenchSourceAsset] = useAsync(
                async () => {
                    const { getForgeWorkbenchSourceAsset } = await import(
                        "adsMagnetoMfc/Api"
                    );
                    return await getForgeWorkbenchSourceAsset(
                        getAdvertiserId(),
                        identifier,
                    );
                },
            );

            React.useEffect(() => {
                void getWorkbenchSourceAsset.execute();
            }, [forgeLastChanged]);

            // Update selected slides when identifier or cart state changes
            React.useEffect(() => {
                const slides = insightInCart
                    ? getSlidesSelected(identifier)
                    : getSlideIndexes(identifier);

                const hasSlides = Array.isArray(slides)
                    ? slides.length > 0
                    : slides.size > 0;
                if (hasSlides || insightInCart) {
                    props.setSelectedSlides(
                        Array.isArray(slides) ? new Set(slides) : slides,
                    );
                }
            }, [identifier, insightInCart, forgeLastChanged]);

            return (
                <BodyComponentBuilder
                    loading={false}
                    bodyComponents={[
                        isWorkbenchForgeEnabled ? (
                            <>
                                {insightInCart ? (
                                    <>
                                        {workbenchSourceAsset.status ===
                                        "success" ? (
                                            <NarrativeLoader
                                                onUpdateNarrative={(
                                                    narrative,
                                                ) => {
                                                    setNumPages(
                                                        narrative
                                                            .visualizationObjectList
                                                            ?.length ?? 0,
                                                    );
                                                }}
                                                allowSelection={
                                                    isWorkbenchForgeEnabled
                                                }
                                                selectedSlides={
                                                    props.selectedSlides
                                                }
                                                onUpdateSelectedSlides={(e) => {
                                                    props.setSelectedSlides(e);
                                                }}
                                                contentIdentifier={identifier}
                                                contentType={
                                                    ForgeContentType.PACKAGED
                                                }
                                                baseNarrative={
                                                    workbenchSourceAsset.result
                                                        .narrative
                                                }
                                                currentNarrative={
                                                    workbenchSourceAsset.result
                                                        .narrative
                                                }
                                                chartCustomizations={
                                                    workbenchSourceAsset.result
                                                        .chartCustomizations
                                                }
                                            />
                                        ) : (
                                            <div></div>
                                        )}
                                    </>
                                ) : addedToCart ? (
                                    <NarrativeLoader
                                        onUpdateNarrative={(narrative) => {
                                            setNumPages(
                                                narrative
                                                    .visualizationObjectList
                                                    ?.length ?? 0,
                                            );
                                        }}
                                        allowSelection={isWorkbenchForgeEnabled}
                                        selectedSlides={props.selectedSlides}
                                        onUpdateSelectedSlides={(e) => {
                                            props.setSelectedSlides(e);
                                        }}
                                        contentIdentifier={identifier}
                                        contentType={ForgeContentType.PACKAGED}
                                        baseNarrative={forgeOutput.narrative}
                                        currentNarrative={forgeOutput.narrative}
                                        chartCustomizations={
                                            forgeOutput.chartCustomizations
                                        }
                                    />
                                ) : (
                                    <NarrativeLoader
                                        onUpdateNarrative={(narrative) => {
                                            setNumPages(
                                                narrative
                                                    .visualizationObjectList
                                                    ?.length ?? 0,
                                            );
                                        }}
                                        allowSelection={isWorkbenchForgeEnabled}
                                        selectedSlides={props.selectedSlides}
                                        onUpdateSelectedSlides={(e) => {
                                            props.setSelectedSlides(e);
                                        }}
                                        contentIdentifier={identifier}
                                        contentType={ForgeContentType.PACKAGED}
                                    />
                                )}
                            </>
                        ) : (
                            <NarrativeLoader
                                onUpdateNarrative={(narrative) => {
                                    setNumPages(
                                        narrative.visualizationObjectList
                                            ?.length ?? 0,
                                    );
                                }}
                                allowSelection={false}
                                selectedSlides={props.selectedSlides}
                                onUpdateSelectedSlides={(e) => {
                                    props.setSelectedSlides(e);
                                }}
                                contentIdentifier={identifier}
                                contentType={ForgeContentType.PACKAGED}
                            />
                        ),
                    ]}
                />
            );
        };
        return {
            type: InsightDetailType.SELECTION,
            component: PackagedInsightBody,
        };
    }, [
        identifier,
        areComponentsReady,
        forgeLastChanged,
        insightInCart,
        addedToCart,
        forgeOutput,
    ]);

    const getDocumentUrlAsync = useCallback(async () => {
        const [forgeClient, blinkClient] = await Promise.all([
            getForgeApiClient(),
            getBlinkApiClientV2(),
        ]);

        const cacheId = startOfHour(new Date()).toISOString();
        const resourceIdentifier = `${identifier}_${cacheId}`;

        try {
            const forgePptxResponse = await forgeClient.getNarrativeAsPptx({
                contentIdentifier: identifier,
                contentType: ForgeContentType.PACKAGED,
            });

            const forgePdfConversionResponse =
                await blinkClient.createAsyncOfficeConvert({
                    clientId: "IHUB",
                    applicationContext: "forge-summary",
                    format: "pdf",
                    resourceIdentifier,
                    inputLocation: forgePptxResponse.s3Location,
                    outputLocation: {
                        bucket: forgePptxResponse.s3Location?.bucket,
                        key: `${resourceIdentifier}.pdf`,
                    },
                });

            const { outputFileDownloadUrl } =
                await blinkClient.pollGetAsyncOfficeConvert({
                    executionId: forgePdfConversionResponse.execution,
                });

            if (isString(outputFileDownloadUrl)) return outputFileDownloadUrl;

            throw new Error(SUMMARY_ASSET_ERROR_MESSAGE);
        } catch (err) {
            console.error("Failed to create asset used for summary:", err);
            const errorMessage =
                err instanceof Error ? err.message : String(err);
            throw new Error(`${SUMMARY_ASSET_ERROR_MESSAGE}: ${errorMessage}`);
        }
    }, [identifier]);

    const PackagedInsightFooterOption: InsightDetailOption = useMemo(() => {
        const PackagedInsightFooter: React.FunctionComponent<
            InsightDetailSelectionProps
        > = (props: InsightDetailSelectionProps) => {
            const [forgeStore, setForgeStore] = React.useState<any>(null);
            const [exportForge, setExportForge] = React.useState<any>(null);

            React.useEffect(() => {
                import("@amzn/ads-forge-web-assets")
                    .then(({ forgeNarrativesStore }) => {
                        setForgeStore(forgeNarrativesStore);
                    })
                    .catch(() => {});
                import("@amzn/ads-forge-web-assets")
                    .then(({ exportNarrativeForge }) => {
                        setExportForge(() => exportNarrativeForge);
                    })
                    .catch(() => {});
            }, []);

            const isDirty = useStore(
                forgeStore || {
                    subscribe: () => () => {},
                    getState: () => ({ isNarrativeDirty: () => false }),
                },
                (s: any) => s.isNarrativeDirty(identifier),
            );

            const exportForgeNarrativeForWorkbench = React.useCallback(
                () => exportForge?.(identifier),
                [exportForge, identifier],
            );

            const selectedSlides = props.selectedSlides;

            const insightWorkbenchCartButtonProps = {
                workbenchScope: getAdvertiserId(),
                insightIdentifier: identifier,
                source: {
                    name: title,
                    slidesSelected: [...selectedSlides],
                    assets: {},
                    origin: {
                        clientId: "IHUB",
                        type: insightType,
                        subType: "",
                    },
                    deckMetadata: {
                        insightIdentifier: identifier,
                        insightType,
                        numPages: numPages ?? 0,
                    },
                    slideMetadata: [],
                },
            };

            return (
                <FooterComponentBuilder
                    loading={false}
                    handleClose={handleClose}
                    identifier={identifier}
                    insightType={insightType}
                    footerComponents={[
                        <SplitButton
                            mainButtonLabel="Download"
                            mainButtonOnClick={() =>
                                handleCustomizedDownloadView(identifier)
                            }
                            menuPosition="top"
                        >
                            <SplitButtonItem
                                value="Download original view"
                                onClick={() =>
                                    handleForgeVizDownload(identifier)
                                }
                            >
                                Download Original View
                            </SplitButtonItem>
                        </SplitButton>,
                        isWorkbenchForgeEnabled ? (
                            <InsightWorkbenchCartButton
                                disabled={
                                    selectedSlides.size === 0 &&
                                    !insightInCart &&
                                    !addedToCart
                                }
                                {...insightWorkbenchCartButtonProps}
                                exportForgeNarrative={
                                    exportForgeNarrativeForWorkbench
                                }
                                forgeNarrativeDirty={isDirty}
                                onSuccess={() => {
                                    setAddedToCart(true);
                                    setForgeOutput(
                                        exportForgeNarrativeForWorkbench(),
                                    );
                                    setForgeLastChanged(
                                        new Date().toISOString(),
                                    );
                                    upsertNotification(
                                        <AddedToGlobalWorkbenchNotification
                                            slidesAdded={selectedSlides.size}
                                        />,
                                        NotificationType.INFO,
                                        identifier,
                                    );
                                }}
                                onUpdate={() => {
                                    setAddedToCart(true);
                                    setForgeOutput(
                                        exportForgeNarrativeForWorkbench(),
                                    );
                                    setForgeLastChanged(
                                        new Date().toISOString(),
                                    );
                                    upsertNotification(
                                        <UpdateGlobalWorkbenchNotification />,
                                        NotificationType.INFO,
                                        identifier,
                                    );
                                }}
                                onError={() => {
                                    upsertNotification(
                                        <GlobalWorkbenchCartActionErrorNotification />,
                                        NotificationType.ERROR,
                                        identifier,
                                    );
                                }}
                            />
                        ) : (
                            <></>
                        ),
                        <EditInputsButton insight={insight} />,
                    ]}
                />
            );
        };
        return {
            type: InsightDetailType.SELECTION,
            component: PackagedInsightFooter,
        };
    }, [identifier, numPages, downloadLoading]);

    const setInsightDetailComponents = useSetInsightDetailComponents({
        identifier,
        familyName: "",
        InsightHeaderOption: PackagedInsightHeaderOption,
        InsightBodyOption: PackagedInsightBodyOption,
        InsightFooterOption: PackagedInsightFooterOption,
        headerReady: areComponentsReady,
        bodyReady: areComponentsReady,
        footerReady: areComponentsReady,
        handleMount,
        handleClose,
    });

    return () => {
        setComponentsReady(true);
        setInsightDetailComponents();
    };
};
